(() => {
  if (!customElements.get('koota-carbon-cycle')) {
    class KootaCarbonCycle extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.controls = Array.from(this.querySelectorAll('[data-kec-cycle-control]'));
        this.panels = Array.from(this.querySelectorAll('[data-kec-cycle-panel]'));
        if (!this.controls.length || this.controls.length !== this.panels.length) return;

        const signal = this.abortController.signal;
        this.controls.forEach((control, index) => {
          control.addEventListener('click', () => this.select(index, true), { signal });
          control.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
            event.preventDefault();
            const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = (index + direction + this.controls.length) % this.controls.length;
            this.select(nextIndex, false);
            this.controls[nextIndex]?.focus();
          }, { signal });
        });

        this.select(0, false);
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      select(index, reveal) {
        if (!Number.isInteger(index) || index < 0 || index >= this.panels.length) return;

        this.controls.forEach((control, controlIndex) => {
          const active = controlIndex === index;
          control.classList.toggle('is-active', active);
          control.closest('.kec-carbon-cycle__step')?.classList.toggle('is-active', active);
          control.setAttribute('aria-pressed', String(active));
          control.setAttribute('aria-expanded', String(active));
        });
        this.panels.forEach((panel, panelIndex) => {
          panel.hidden = panelIndex !== index;
        });

        if (reveal) {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          requestAnimationFrame(() => {
            this.panels[index]?.scrollIntoView({
              behavior: reducedMotion ? 'auto' : 'smooth',
              block: 'nearest'
            });
          });
        }
      }
    }

    customElements.define('koota-carbon-cycle', KootaCarbonCycle);
  }

  if (!customElements.get('koota-carbon-chart')) {
    class KootaCarbonChart extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.controls = Array.from(this.querySelectorAll('[data-kec-carbon-control]'));
        this.segments = Array.from(this.querySelectorAll('[data-kec-carbon-segment]'));
        this.panels = Array.from(this.querySelectorAll('[data-kec-carbon-panel]'));
        if (!this.controls.length || !this.panels.length) return;

        const signal = this.abortController.signal;
        const selectFrom = (element, options = {}) => this.select(Number(element.dataset.kecCarbonIndex), options);

        this.controls.forEach((control) => {
          control.addEventListener('click', () => selectFrom(control, { reveal: true }), { signal });
        });

        this.segments.forEach((segment) => {
          segment.addEventListener('click', () => selectFrom(segment, { reveal: true }), { signal });
        });

        const initiallySelected = this.controls.findIndex((control) => control.classList.contains('is-active'));
        this.select(initiallySelected >= 0 ? initiallySelected : 0);
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      select(index, { reveal = false } = {}) {
        if (!Number.isInteger(index) || index < 0 || index >= this.panels.length) return;

        const selectedPanel = this.panels[index];

        this.controls.forEach((control, controlIndex) => {
          const active = controlIndex === index;
          control.classList.toggle('is-active', active);
          control.setAttribute('aria-pressed', String(active));
          control.setAttribute('aria-expanded', String(active));
        });

        this.segments.forEach((segment, segmentIndex) => {
          const active = segmentIndex === index;
          segment.classList.toggle('is-active', active);
        });

        this.panels.forEach((panel, panelIndex) => {
          panel.hidden = panelIndex !== index;
        });

        if (reveal) {
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          requestAnimationFrame(() => {
            selectedPanel?.scrollIntoView({
              behavior: reducedMotion ? 'auto' : 'smooth',
              block: 'nearest'
            });
          });
        }
      }
    }

    customElements.define('koota-carbon-chart', KootaCarbonChart);
  }

  if (customElements.get('koota-carbon-equivalences')) return;

  class KootaCarbonEquivalences extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.range = this.querySelector('[data-kec-carbon-cups]');
      this.rangeOutput = this.querySelector('[data-kec-carbon-cups-output]');
      this.totalOutput = this.querySelector('[data-kec-carbon-total]');
      this.annualCupsOutput = this.querySelector('[data-kec-carbon-annual-cups]');
      this.bagsOutput = this.querySelector('[data-kec-carbon-bags]');
      this.cards = Array.from(this.closest('.kec-carbon-chapter')?.querySelectorAll('[data-equivalence-factor]') || []);

      const signal = this.abortController.signal;
      this.range?.addEventListener('input', () => this.update(), { signal });
      this.update();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    format(value, precision = 2) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision
      }).format(value);
    }

    update() {
      const cupsPerDay = Number(this.range?.value || 1);
      const gramsPerCup = Number(this.dataset.withValue) || 0;
      const cupDose = Number(this.dataset.cupDose) || 7;
      const bagWeight = Number(this.dataset.bagWeight) || 250;
      const annualCups = cupsPerDay * 365;
      const annualTotalGrams = gramsPerCup * annualCups;
      const annualBags = (annualCups * cupDose) / bagWeight;

      if (this.rangeOutput) this.rangeOutput.textContent = `${cupsPerDay} ${cupsPerDay > 1 ? 'tasses' : 'tasse'}/jour`;
      if (this.totalOutput) this.totalOutput.textContent = this.format(annualTotalGrams / 1000, 2);
      if (this.annualCupsOutput) this.annualCupsOutput.textContent = this.format(annualCups, 0);
      if (this.bagsOutput) this.bagsOutput.textContent = this.format(annualBags, 1);

      this.cards.forEach((card) => {
        const factor = Number(card.dataset.equivalenceFactor || 0);
        const precision = Number(card.dataset.equivalencePrecision || 1);
        const output = card.querySelector('[data-kec-carbon-equivalent]');
        if (output) output.textContent = factor > 0 ? this.format(annualTotalGrams / factor, precision) : '—';
      });
    }
  }

  customElements.define('koota-carbon-equivalences', KootaCarbonEquivalences);
})();
